import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DailyForecastControl.css';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import useDimensionOptions from '../../hooks/useDimensionOptions';
import { deriveRegionFromLocation, deriveGranularityFromLocation } from '../../utils_1/locationRouting';
import useSharedForecastFilters from '../../hooks/useSharedForecastFilters';
import { useAuth } from '../../context/AuthContext';
import { applyRoleLocks, getRoleLocks } from '../../utils_1/roleLocks';

const DailyForecastControl = ({ onDailyForecast, loading }) => {
  const deriveForecastDays = (start, end) => {
    if (!start || !end) return 30; // Allow missing dates so backend fallback triggers
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) {
      throw new Error('EndDate  must be on or after Start Date.');
    }
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.round((e - s) / msPerDay) + 1; // inclusive
  };

  const [params, setParams] = useSharedForecastFilters([
    'daily_forecast_filters',
    'promotional_analysis_filters',
    'scenario_panel_filters',
  ]);
  const { user } = useAuth();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);
  const brandLocked = Boolean(roleLocks.lockedBrand);
  const channelLocked = Boolean(roleLocks.lockedChannel);

  // Shared dimension options hook (brands, SKUs, channels, locations)
  const { options, selectedBrand, setSelectedBrand, optionsLoading } = useDimensionOptions(
    brandLocked ? roleLocks.lockedBrand : (params.brand || '')
  );
  const regionOptions = options.regions && options.regions.length > 0 ? options.regions : [];
  const displayBrand = brandLocked ? roleLocks.lockedBrand : params.brand;
  const brandOptions = brandLocked && roleLocks.lockedBrand && !options.brands.includes(roleLocks.lockedBrand)
    ? [roleLocks.lockedBrand, ...options.brands]
    : options.brands;
  const channelOptions = channelLocked && roleLocks.lockedChannel && !options.channels.includes(roleLocks.lockedChannel)
    ? [roleLocks.lockedChannel, ...options.channels]
    : options.channels;

  const autoLoadedRef = useRef(false);

  useEffect(() => {
    // Pre-fill defaults once, but do not auto-run the chart.
    if (!autoLoadedRef.current && !optionsLoading) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const endDate = new Date(tomorrow.getTime() + 27 * 24 * 60 * 60 * 1000);
      const defaultSku = options.skus?.[0] || '';

      setParams(prev => {
        const hasDates = Boolean(prev.start_date && prev.end_date);
        return {
          ...prev,
          sku_id: prev.sku_id || defaultSku,
          start_date: hasDates ? prev.start_date : tomorrow.toISOString().split('T')[0],
          end_date: hasDates ? prev.end_date : endDate.toISOString().split('T')[0],
          forecast_days: hasDates ? (prev.forecast_days || deriveForecastDays(prev.start_date, prev.end_date)) : 28,
        };
      });
      autoLoadedRef.current = true;
    }
  }, [options.skus, optionsLoading, params.start_date, params.end_date, setParams]);

  useEffect(() => {
    if (optionsLoading || regionOptions.length === 0) {
      return;
    }

    setParams(prev => {
      if (prev.region && regionOptions.includes(prev.region)) {
        return prev;
      }
      const nextRegion = regionOptions[0];
      return nextRegion && nextRegion !== prev.region
        ? { ...prev, region: nextRegion }
        : prev;
    });
  }, [optionsLoading, regionOptions, setParams]);


  // Sync selectedBrand with params.brand, and clear sku_id when brand changes
  useEffect(() => {
    if (brandLocked) {
      if (selectedBrand !== roleLocks.lockedBrand) {
        setSelectedBrand(roleLocks.lockedBrand);
      }
      if (params.brand !== roleLocks.lockedBrand) {
        setParams(prev => ({ ...prev, brand: roleLocks.lockedBrand, sku_id: '' }));
      }
      return;
    }
    if (selectedBrand !== params.brand) {
      setParams(prev => ({ ...prev, brand: selectedBrand, sku_id: '' }));
    }
  }, [brandLocked, roleLocks.lockedBrand, selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setParams(prev => {
      const next = applyRoleLocks(prev, role);
      const unchanged = Object.keys(next).every(key => next[key] === prev[key]);
      return unchanged ? prev : next;
    });
  }, [role, setParams]);

  const [error, setError] = useState(null);

  const handleChange = useCallback((param, value) => {
    if (param === 'start_date' || param === 'end_date') {
      const otherValue = param === 'start_date' ? params.end_date : params.start_date;
      if (value && otherValue) {
        const start = param === 'start_date' ? new Date(value) : new Date(otherValue);
        const end = param === 'end_date' ? new Date(value) : new Date(otherValue);
        if (start > end) {
          window.alert("Start date cannot be after end date.");
          return;
        }
      }
    }

    setParams(prev => {
      const next = { ...prev, [param]: value };

      // Auto-sync Region when Location changes
      if (param === 'location' && value) {
        const derived = deriveRegionFromLocation(value, options.locationRegionMap, options.regions);
        if (derived) next.region = derived;
      }
      
      // Auto-sync Brand when SKU changes
      if (param === 'sku_id' && value) {
        const derivedBrand = options.skuBrandMap?.[value];
        if (derivedBrand && !brandLocked && derivedBrand !== prev.brand) {
          next.brand = derivedBrand;
          setTimeout(() => setSelectedBrand(derivedBrand), 0);
        }
      }

      if ((param === 'start_date' || param === 'end_date') && next.start_date && next.end_date) {
        try {
          next.forecast_days = deriveForecastDays(next.start_date, next.end_date);
          setError(null);
        } catch (err) {
          setError(err.message);
        }
      } else if (param === 'forecast_days') {
        const days = parseInt(value);
        if (!isNaN(days) && days > 0 && next.start_date) {
          const start = new Date(next.start_date);
          const end = new Date(start.getTime() + (days - 1) * 24 * 60 * 60 * 1000);
          next.end_date = end.toISOString().split('T')[0];
        }
      }
      return next;
    });
  }, [params.end_date, params.start_date, setParams]);

  const setQuickDate = (days) => {
    const start = new Date();
    start.setDate(start.getDate() + 1);
    const end = new Date(start.getTime() + (days - 1) * 24 * 60 * 60 * 1000);
    
    setParams(prev => ({
      ...prev,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0],
      forecast_days: days
    }));
    setError(null);
  };


  const handleSubmit = () => {
    try {
      const days = deriveForecastDays(params.start_date, params.end_date);
      const defaultSku = params.sku_id || options.skus?.[0] || '';
      const next = {
        ...params,
        sku_id: defaultSku,
        forecast_days: days
      };
      setParams(next);
      setError(null);
      onDailyForecast(next);
    } catch (err) {
      setError(err.message);
      window.alert(err.message);
    }
  };

  return (
    <div className="control-panel card">
      <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <h3 className="panel-title">Daily Forecast</h3>
        <div className="preset-buttons">
          <span className="preset-label" style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
            Quick Select:
            <HelpTooltip content="Presets for common forecasting horizons." />
          </span>
          <button className="btn-preset" onClick={() => setQuickDate(7)}>7D</button>
          <button className="btn-preset" onClick={() => setQuickDate(14)}>14D</button>
          <button className="btn-preset" onClick={() => setQuickDate(30)}>30D</button>
          <button className="btn-preset" onClick={() => setQuickDate(90)}>90D</button>
        </div>
      </div>
      <div className="tab-content">
        <div className="form-grid grid-4">
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Brand <HelpTooltip content="Select a brand to narrow down the SKU list below." /></label>
            <select
              value={displayBrand}
              onChange={(e) => { handleChange('brand', e.target.value); setSelectedBrand(e.target.value); }}
              className="form-input"
              disabled={brandLocked}
            >
              <option value="">All Brands (Optional)</option>
              {brandOptions.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>SKU ID <HelpTooltip content="Select a specific product to forecast, or leave as 'All SKUs'. Pick a Brand first to filter this list." /></label>
            <select
              value={params.sku_id}
              onChange={(e) => handleChange('sku_id', e.target.value)}
              className="form-input"
            >
              <option value="">All SKUs (Optional)</option>
              {options.skus.map(sku => (
                <option key={sku} value={sku}>{sku}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Channel <HelpTooltip content="Select a specific sales channel, or leave as 'All Channels'." /></label>
            <select
              value={params.channel}
              onChange={(e) => handleChange('channel', e.target.value)}
              className="form-input"
              disabled={channelLocked}
            >
              <option value="">All Channels (Optional)</option>
              {channelOptions.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Location <HelpTooltip content="Select a specific geographical location, or leave as 'All Locations'." /></label>
            <select
              value={params.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="form-input"
            >
              <option value="">All Locations (Optional)</option>
              {options.locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Forecast Granularity <HelpTooltip content="Choose the level of detail for the forecast output (e.g. daily, weekly, monthly)." /></label>
            <select
              value={params.forecast_granularity || 'daily'}
              onChange={(e) => handleChange('forecast_granularity', e.target.value)}
              className="form-input"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Granularity <HelpTooltip content="Choose the level of detail for the forecast output." /></label>
            <select
              value={params.granularity_level}
              onChange={(e) => handleChange('granularity_level', e.target.value)}
              className="form-input"
            >
              <option value="sku_channel_location">SKU + Channel + Location</option>
              <option value="sku_channel">SKU + Channel</option>
              <option value="sku">SKU Only</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Start Date <HelpTooltip content="The beginning date for the forecast." /></label>
            <input
              type="date"
              value={params.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>End Date <HelpTooltip content="The ending date for the forecast." /></label>
            <input
              type="date"
              value={params.end_date}
              onChange={(e) => handleChange('end_date', e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Region <HelpTooltip content="Select the business region to apply specific growth and promo rules." /></label>
            <select
              value={params.region}
              onChange={(e) => handleChange('region', e.target.value)}
              className="form-input"
            >
              {!regionOptions.length && <option value="">Loading regions...</option>}
              {regionOptions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Forecast Days <HelpTooltip content="Calculated number of days between the start and end dates." /></label>
            <input
              type="number"
              min="1"
              value={params.forecast_days || ''}
              onChange={(e) => handleChange('forecast_days', e.target.value)}
              className="form-input"
            />
          </div>
        </div>
        <div className="form-actions">
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Generate Daily Forecast'}
          </button>
          {error && <div className="form-error">{error}</div>}
        </div>
      </div>

    </div>
  );
};

export default DailyForecastControl;
